import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SupplierService} from '@secca/core/services/supplier.service';
import {DocListItem, SupplierSearchResponse} from '@secca/shared/models/SupplierSearchResponse';
import { AutoUnsubscribe } from '@secca/shared/decorators/auto-unsubscribe';
import { DictionaryService } from '@secca/core/services/dictionary.service';
import {combineLatest, Observable} from 'rxjs';
import {
  SupplierSearchData
} from '@secca/case/components/case-plans/case-plans/add-service-order/search-service-provider/SupplierDialogData';
import { StakeholderType } from '@secca/case/components/case-stakeholders/stakeholderType';
import { StakeholderService } from '@secca/core/services/stakeholder.service';
import {
  SupplierSearchHelper
} from '@secca/case/components/case-plans/case-plans/add-service-order/search-service-provider/search-service-provider-modal/supplier-search-helper.service';
import {
  PartnerService
} from '@secca/case/components/case-plans/case-plans/add-service-order/search-service-provider/search-service-provider-modal/partner.service';
import { PhoneNumberType, StakeholderTypeEnum } from '@secca/shared/models/enums';
import { PhoneService } from '@secca/core/services/phone.service';
import { CaseService } from '@secca/core/services/case.service';


@Component({
    selector: 'app-supplier-search-dialog',
    templateUrl: './supplier-search-dialog.component.html',
    styleUrls: ['./supplier-search-dialog.component.scss'],
    standalone: false
})
@AutoUnsubscribe
export class SupplierSearchDialogComponent implements OnChanges {

  constructor(
    private supplierService: SupplierService,
    private stakeholderService: StakeholderService,
    private dictionaryService: DictionaryService,
    private supplierSearchHelper: SupplierSearchHelper,
    private partnerService: PartnerService,
    private phoneService: PhoneService,
    private caseService: CaseService
  ) {
  }

  @Output()
  closeSearchSupplierModal = new EventEmitter<string>();
  @Input()
  supplierSearchData: SupplierSearchData;

  suppliers: DocListItem[] = [] as DocListItem[];
  isErrors = false;
  message = '';
  foundAnySuppliers = true;
  noDataFound = false;
  isLoading = false;

  expandGeneral = true;
  expandFinancialAgreements = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.supplierSearchData) {
      this.isLoading = true;
      this.findSupplier(this.supplierSearchData);
    }
  }

  close() {
    this.closeSearchSupplierModal.emit('close');
  }

  findSupplier(supplierSearchData: SupplierSearchData) {
    const supplierSearch = `freeText=supplierId:${supplierSearchData?.supplierId}`;
    let suppliersObservable: Observable<SupplierSearchResponse>;
    if (supplierSearchData.supplierId) {
      suppliersObservable = this.supplierService.searchSuppliers(supplierSearch);
      this.expandGeneral = true;
      this.expandFinancialAgreements = false;
    } else {
      suppliersObservable = this.supplierService.getSupplierFromCreditor(supplierSearchData.creditorId.toString());
      this.expandGeneral = false;
      this.expandFinancialAgreements = true;
    }
    const stakeholderTypesObservable = this.dictionaryService.getStakeholdersTypes();

    combineLatest([suppliersObservable, stakeholderTypesObservable]).subscribe({
      next: ([res, stakeholderTypes]) => {
        if (res.numFound === 0) {
          this.isLoading = false;
          this.foundAnySuppliers = false;
          this.noDataFound = true;
          this.message = new Date().toLocaleDateString() + ', ' + new Date().toLocaleTimeString() + ', ' + supplierSearch;
          console.error('Error on fetching data', supplierSearch);
          return;
        }
        this.suppliers = res.docList;
        this.updateStakeholderFromSupplierInNMS(this.suppliers, this.supplierSearchData);
        this.supplierSearchHelper.markSuppliersAsBlacklisted(this.suppliers);
        this.partnerService.setAssistantPartnerOnSupplier(this.suppliers, this.supplierSearchData.caseId);
        this.suppliers.forEach(supplier => {
          supplier.iconPath = this.findIconPathForStakeholder(stakeholderTypes, +supplier.supplierTypeCode)
          ;
        });
      },
      error: (error) => {
        this.isLoading = false;
        this.foundAnySuppliers = false;
        this.isErrors = true;
        this.message = new Date().toLocaleDateString() + ', ' + new Date().toLocaleTimeString() + ' ' + error.message;
        console.error('Error on fetching data', error);
      }
    });
  }

  private updateStakeholderFromSupplierInNMS(suppliers: DocListItem[], searchData: SupplierSearchData) {
    if (!suppliers || !suppliers.length) {
      return;
    }
    const stakeholder = searchData?.caseStakeholder;
    if (!stakeholder) {
      return;
    }
    if (!stakeholder.hasRole(StakeholderTypeEnum.medicalAdvisor) && !stakeholder.hasRole(StakeholderTypeEnum.escortDoctor)) {
      return;
    }

    const supplier = suppliers[0];
    if (!this.isMedicalEscortOrMedicalAdvisor(supplier)) {
      return;
    }
    let updated = false;

    if (supplier.contact_list) {
      const emailContacts = supplier.contact_list.filter(contact => this.isEmailContact(contact));
      const extractedEmails: string[] = [];
      emailContacts.forEach(contact => {
        const emailStr = this.extractContactEmail(contact);
        if (emailStr) {
          extractedEmails.push(emailStr);
        }
      });

      if (extractedEmails.length > 0) {
        let chosenEmail = extractedEmails[0];
        if (extractedEmails.length > 1) {
          const sosEuEmail = extractedEmails.find(e => e.toLowerCase().includes('@sos.eu'));
          if (sosEuEmail) {
            chosenEmail = sosEuEmail;
          }
        }

        const currentEmail = this.getStakeholderEmail(stakeholder);
        if (currentEmail !== chosenEmail) {
          this.setStakeholderEmail(stakeholder, chosenEmail);
          stakeholder.emailUpdatedFromNms = true;
          updated = true;
        }
      }

      supplier.contact_list.forEach(contact => {
        if (this.isEmailContact(contact)) {
          return;
        }

        const phoneStr = this.extractContactPhone(contact);
        if (phoneStr) {
          const phoneDto = this.phoneService.extractPhoneDto(phoneStr);
          if (phoneDto && phoneDto.phoneNumber) {
            if (this.isMobileContact(contact)) {
              if (this.updateStakeholderPhone(stakeholder, PhoneNumberType.Mobile, phoneDto)) {
                stakeholder.phoneUpdatedFromNms = true;
                updated = true;
              }
            }
          }
        }
      });
    }

    if (updated) {
      this.caseService.updateStakeholderForCase(stakeholder);
    }
  }

  private isEmailContact(contact: any): boolean {
    const contactType = contact.contact_type?.toUpperCase();
    const fullName = contact.contact_fullName?.toUpperCase();
    const lastName = contact.contact_lastName?.toUpperCase();
    return (
      contactType === 'EMAIL' ||
      Boolean(contact.contact_email) ||
      Boolean(fullName?.startsWith('EMAIL')) ||
      Boolean(lastName?.startsWith('EMAIL'))
    );
  }

  private extractContactEmail(contact: any): string {
    return (
      contact.contact_email ||
      contact.contact_lastName?.split(' ')[1] ||
      contact.contact_fullName?.split(' ')[1]
    )?.trim();
  }

  private isMobileContact(contact: any): boolean {
    const contactType = contact.contact_type?.toUpperCase();
    const fullName = contact.contact_fullName?.toUpperCase();
    const lastName = contact.contact_lastName?.toUpperCase();
    return (
      Boolean(contactType?.includes('MOBILE')) ||
      Boolean(fullName?.includes('MOBILE')) ||
      Boolean(lastName?.includes('MOBILE'))
    );
  }


  private extractContactPhone(contact: any): string {
    return (
      contact.contact_mobile ||
      contact.contact_telephone ||
      contact.contact_lastName?.split(' ')[1] ||
      contact.contact_fullName?.split(' ')[1]
    );
  }

  private getStakeholderEmailContainer(stakeholder: any): any {
    return stakeholder.company || stakeholder.person || stakeholder;
  }

  private getStakeholderEmail(stakeholder: any): string {
    return this.getStakeholderEmailContainer(stakeholder)?.email;
  }

  private setStakeholderEmail(stakeholder: any, email: string): void {
    const container = this.getStakeholderEmailContainer(stakeholder);
    container.email = email;
  }

  private updateStakeholderPhone(
    stakeholder: any,
    phoneType: PhoneNumberType,
    phoneDto: { phoneNumber: string; phonePrefix: string; countryCode: string }
  ): boolean {
    const existingPhone = this.stakeholderService.getPhoneNumber(phoneType, stakeholder.phoneNumbers);
    if (!existingPhone || existingPhone.number !== phoneDto.phoneNumber || existingPhone.prefix !== phoneDto.phonePrefix) {
      stakeholder.phoneNumbers = this.stakeholderService.setPhonenumber(
        phoneType,
        {
          phoneNumber: phoneDto.phoneNumber,
          phonePrefix: phoneDto.phonePrefix,
          phoneCountryCode: phoneDto.countryCode
        },
        stakeholder.phoneNumbers
      );
      return true;
    }
    return false;
  }

  private isMedicalEscortOrMedicalAdvisor(supplier: DocListItem): boolean {
    if (!supplier) {
      return false;
    }
    const supplierType = supplier.supplierType?.toLowerCase() || '';
    const serviceTypes = (supplier.serviceTypes || []).map(s => s.toLowerCase());
    const supplierServices = (supplier.supplierServices || []).map(s => s.toLowerCase());

    return (
      supplier.supplierTypeCode === 8 ||
      supplier.supplierTypeCode === 9 ||
      supplierType.includes('escort') ||
      supplierType.includes('medical advisor') ||
      supplierType.includes('medical-advisor') ||
      supplierType.includes('medicaladvisor') ||
      supplierType.includes('advisor') ||
      serviceTypes.some(st => st.includes('escort') || st.includes('medical advisor') || st.includes('medical-advisor') || st.includes('medicaladvisor') || st.includes('advisor')) ||
      supplierServices.some(ss => ss.includes('escort') || ss.includes('medical advisor') || ss.includes('medical-advisor') || ss.includes('medicaladvisor') || ss.includes('advisor'))
    );
  }

  private findIconPathForStakeholder(stakeholdersTypes: StakeholderType[], supplierTypeCode: number): string {
    const typeCode = supplierTypeCode;
    const stakeholderType = stakeholdersTypes.find(st => st.providerTypeId === typeCode);
    return this.stakeholderService.getStakeholderIconFullPath(stakeholderType?.stakeholderTypeEnum) || '/assets/icons/service-order-type/other.svg';
  }

}
